import React, { useState, useEffect, useRef } from "react";
import { object, array, number, string } from "prop-types";
import { StoryElement, SocialShare, Link } from "@quintype/components";
import DmCustomEmbed from "@dmvs-apac/dm-custom-embed-react";
import { connect } from "react-redux";
import get from "lodash/get";

import Headline from "../../atoms/Headline";
import CustomStoryCard5 from "../../molecules/CustomStoryCard5";
import SocialShareTemplate from "../../molecules/SocialShareTemplate";
import StoryPageMetaData from "../../molecules/StoryPageMetaData";
import MetypeCommentWrapper from "../../MetypeComments";
import StoryTag from "../StoryTag";
import DfpAd from "../../dfp-ad";
import { staticTexts } from "../../utils/Translate";
import BookmarkViewIcon from "../../atoms/BookmarkViewIcon";
import { Share } from "../../atoms/icons/StoryPageIcons";
import ShareAllTemplate from "../../molecules/ShareAllTemplate";

import "./video.m.css";

const VideoStoryTemplate = ({
  story,
  relatedStories,
  metypeConfig,
  publisherAttributes,
  jwtToken,
  authHost,
  member,
  scrollIndex,
  dailyMotion,
}) => {
  const [isSocialShare, setSocialShare] = useState(false);
  const ref = useRef(null);
  const sectionName = get(story, ["sections", "0"], "");

  const getFirstVideoElement = story.cards
    .map((item) => item["story-elements"].filter((element) => element.type === "youtube-video"))
    .flat();

  const getDailyMotionFirstVideoElement =
    getFirstVideoElement.length === 0 &&
    story.cards
      .map((item) => item["story-elements"].filter((element) => element.subtype === "dailymotion-video"))
      .flat();

  const subHeadlineStyle = `${publisherAttributes.lang}-sub-headline`;

  const toggleShare = () => setSocialShare(!isSocialShare);

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setSocialShare(false);
    }
  };

  useEffect(() => {
    if (global.deviceWidth > 876) {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }
  });

  return (
    <div>
      <div styleName="ad-slot" className="top-ad">
        <DfpAd
          adtype={publisherAttributes === "en" ? "EnPalo_ROS_728x90_T1" : "News_728x90_T1"}
          targetStory={story}
          slotId={`story_${scrollIndex}-t1`}
        />
      </div>
      <div styleName="wrapper">
        <div>
          {getFirstVideoElement.length > 0 ? (
            <div styleName="primary-video-wrapper">
              <StoryElement element={getFirstVideoElement[0]} story={story} externalLink={true} />
            </div>
          ) : (
            <div styleName="primary-video-wrapper daily-motion-wrapper">
              <DmCustomEmbed
                playerid={dailyMotion.playerId}
                sort="recent"
                owners={dailyMotion.channelKey}
                videoid={getDailyMotionFirstVideoElement[0].metadata["video-id"]}
              />
            </div>
          )}
          {story.subheadline && (
            <Headline
              text={story.subheadline}
              headlineType={16}
              headerLevel={2}
              className="story-sub-heading"
              styleName={subHeadlineStyle}
            />
          )}
          <Headline
            text={story.headline}
            headlineType={35}
            headerLevel={1}
            className={`story-headline ${publisherAttributes.lang}-story-headline`}
          />
          <div styleName="meta-data">
            <StoryPageMetaData story={story} lang={publisherAttributes.lang} showSocialShare={false} />
          </div>
          <div styleName="bottom-icons">
            <SocialShare template={SocialShareTemplate} title={story.headline} fullUrl={encodeURI(story.url)} />
            <span styleName="share-button" ref={ref}>
              <span onClick={toggleShare}>
                <Share color1="#eee" color2="#222" />
              </span>
              {isSocialShare && (
                <SocialShare
                  template={ShareAllTemplate}
                  title={story.headline}
                  fullUrl={encodeURI(story.url)}
                  toggleShare={toggleShare}
                />
              )}
            </span>
            {publisherAttributes.showBookmark && (
              <span styleName="bookmark">
                <BookmarkViewIcon story={story} />
              </span>
            )}
          </div>
          {story.tags && (
            <div styleName="tags">
              {sectionName && (
                <Link
                  href={sectionName["section-url"]}
                  styleName="sub-section-name"
                  data-tracking-category="Story Sub Section"
                  data-tracking-action="Click"
                  data-tracking-label={sectionName["display-name"]}
                >
                  {publisherAttributes.lang === "en" ? "Watch more from " : ""}
                  <span styleName="section-name">{sectionName["display-name"]}</span>
                  {publisherAttributes.lang === "bn" ? " থেকে আরও দেখুন" : ""}
                </Link>
              )}
              <StoryTag tags={story.tags} />
            </div>
          )}
          <div styleName="metype-wrapper">
            {publisherAttributes.showMetype && (
              <div styleName="comments">{staticTexts(publisherAttributes.lang).doComment}</div>
            )}
            <div styleName="social-share">
              <SocialShare template={SocialShareTemplate} title={story.headline} fullUrl={encodeURI(story.url)} />
              {publisherAttributes.showBookmark && (
                <span>
                  <BookmarkViewIcon story={story} />
                </span>
              )}
            </div>
          </div>
          <div styleName="metype-section">
            {publisherAttributes.showMetype && (
              <MetypeCommentWrapper
                metypeConfig={metypeConfig}
                storyUrl={story.url}
                jwtToken={jwtToken}
                authHost={authHost}
                lang={publisherAttributes.lang}
                member={member}
              />
            )}
          </div>
        </div>
        <div styleName="content-wrapper">
          <div styleName="side-ad">
            <DfpAd
              adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_336x280_R1" : "News_336x280_R1"}
              targetStory={story}
              slotId={`story_${scrollIndex}-r1`}
            />
          </div>
          <div styleName="next-video">{staticTexts(publisherAttributes.lang).nextVideo}</div>
          {relatedStories.length > 0 &&
            relatedStories
              .slice(0, 4)
              .map(
                (story, index) =>
                  story.story["story-template"] === "video" && (
                    <CustomStoryCard5
                      key={index}
                      story={story.story}
                      className="related-stories"
                      imageType="image4x3"
                      aspectRatio={[4, 3]}
                      headlineType={16}
                      displayStoryIcon={true}
                    />
                  )
              )}
        </div>
        <div styleName="horizontal-ad">
          <DfpAd
            adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_728x90_B2" : "News_728x90_B2"}
            targetStory={story}
            slotId={`story_${scrollIndex}-b2`}
          />
        </div>
      </div>
    </div>
  );
};

const VideoStory = ({
  story,
  relatedStories,
  metypeConfig,
  publisherAttributes,
  domainSlug,
  authHost,
  member,
  jwtToken,
  scrollIndex,
  dailyMotion,
}) => (
  <div className="story-grid">
    <VideoStoryTemplate
      story={story}
      relatedStories={relatedStories}
      metypeConfig={metypeConfig}
      publisherAttributes={publisherAttributes}
      domainSlug={domainSlug}
      authHost={authHost}
      member={member}
      jwtToken={jwtToken}
      scrollIndex={scrollIndex}
      dailyMotion={dailyMotion}
    />
  </div>
);

VideoStoryTemplate.propTypes = {
  story: object,
  metypeConfig: object,
  relatedStories: array,
  publisherAttributes: object,
  jwtToken: string,
  domainSlug: string,
  authHost: string,
  member: object,
  scrollIndex: number,
  dailyMotion: object,
};

VideoStory.propTypes = {
  story: object,
  metypeConfig: object,
  relatedStories: array,
  publisherAttributes: object,
  domainSlug: string,
  authHost: string,
  member: object,
  jwtToken: string,
  scrollIndex: number,
  dailyMotion: object,
};

const mapStateToProps = (state) => ({
  domainSlug: get(state, ["qt", "domainSlug"]),
  metypeConfig: get(state, ["qt", "config", "publisher-attributes", "metypeConfig"], {}),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  jwtToken: get(state, ["userReducer", "jwt_token"], null),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  member: state.member,
  dailyMotion: get(state, ["qt", "config", "publisher-attributes", "dailyMotion"], {
    playerId: "x1qz1",
    channelKey: "ProthomAlo",
  }),
});

export default connect(mapStateToProps, "")(VideoStory);
